<template>
  <div v-show="show" class="modal-overlay" @click.self="handleBackdropClick">
    <!-- Auth Modal Content -->
    <div class="auth-container auth-modal">
      <!-- Confetti Canvas for Auth Modal -->
      <canvas 
        ref="confettiCanvas" 
        class="confetti-canvas-container"
      ></canvas>
      <!-- Header -->
      <div class="auth-header">
        <div class="auth-logo">
          <i class="fa-solid fa-user-shield"></i>
        </div>
        <h1 class="auth-title">{{ title }}</h1>
        <button class="btn-close-custom" @click="handleClose">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="auth-form-body">

        <!-- SIGN IN -->
        <form v-if="state === 'signin'" @submit.prevent="onSignIn">
          <input 
            class="form-control form-control-lg mb-3" 
            type="email"
            placeholder="Email" 
            v-model="form.email" 
            required
          />
          <input 
            class="form-control form-control-lg mb-3" 
            type="password" 
            placeholder="Password" 
            v-model="form.password" 
            required
          />
          <ErrorAlert :error="error" />

          <button 
            type="submit"
            class="btn btn-primary btn-lg w-100 mb-2" 
            :disabled="isLoading"
          >
            <span v-if="isLoading">
              <i class="fa-solid fa-spinner fa-spin me-2"></i>
              Signing In...
            </span>
            <span v-else>Sign In</span>
          </button>

          <button class="btn btn-link w-100 mb-2" type="button" @click="send('FORGOT_REQUEST')">
            Forgot Password
          </button>
          <button class="btn btn-link w-100" type="button" @click="send('SIGNUP_REQUEST')">
            Create Account
          </button>
        </form>

        <!-- SIGN UP -->
        <form v-if="state === 'signup'" @submit.prevent="onSignUp">
          <input class="form-control form-control-lg mb-3" type="email" placeholder="Email" v-model="form.email" required />
          <input class="form-control form-control-lg mb-3" type="password" placeholder="Password" v-model="form.password" required />
          <input class="form-control form-control-lg mb-3" type="password" placeholder="Confirm Password"
            v-model="form.confirmPassword" required />

          <ErrorAlert :error="error" />

          <button class="btn btn-primary btn-lg w-100 mb-2" :disabled="form.password !== form.confirmPassword || isLoading">
            <span v-if="isLoading">
              <i class="fa-solid fa-spinner fa-spin me-2"></i>
              Creating Account...
            </span>
            <span v-else>Create Account</span>
          </button>

          <button class="btn btn-link w-100" type="button" @click="send('BACK')">
            Back to Sign In
          </button>
        </form>

        <!-- CONFIRM -->
        <form v-if="state === 'confirm'" @submit.prevent="onConfirm">
          <div class="alert alert-info mb-3">
            <i class="fa-solid fa-envelope me-2"></i>
            Enter the verification code sent to your email.
          </div>

          <input class="form-control form-control-lg mb-3 text-center" placeholder="Verification Code" v-model="form.confirmationCode" required />

          <ErrorAlert :error="error" />

          <button class="btn btn-primary btn-lg w-100 mb-2" :disabled="isLoading">
            <span v-if="isLoading">
              <i class="fa-solid fa-spinner fa-spin me-2"></i>
              Verifying...
            </span>
            <span v-else>Verify Email</span>
          </button>
        </form>

        <!-- FORGOT -->
        <form v-if="state === 'forgot'" @submit.prevent="onForgot">
          <div class="alert alert-info mb-3">
            <i class="fa-solid fa-info-circle me-2"></i>
            Enter your email to receive a password reset code.
          </div>
          
          <input class="form-control form-control-lg mb-3" type="email" placeholder="Email" v-model="form.email" required />
          <ErrorAlert :error="error" />

          <button class="btn btn-primary btn-lg w-100 mb-2" :disabled="isLoading">
            <span v-if="isLoading">
              <i class="fa-solid fa-spinner fa-spin me-2"></i>
              Sending...
            </span>
            <span v-else>Send Reset Code</span>
          </button>
          
          <button class="btn btn-link w-100" type="button" @click="send('BACK')">
            Back to Sign In
          </button>
        </form>

        <!-- RESET -->
        <form v-if="state === 'reset'" @submit.prevent="onReset">
          <div class="alert alert-info mb-3">
            <i class="fa-solid fa-key me-2"></i>
            Enter the reset code and your new password.
          </div>
          
          <input class="form-control form-control-lg mb-3 text-center" placeholder="Reset Code" v-model="form.resetCode" required />
          <input class="form-control form-control-lg mb-3" type="password" placeholder="New Password" v-model="form.newPassword" required />

          <ErrorAlert :error="error" />

          <button class="btn btn-primary btn-lg w-100 mb-2" :disabled="isLoading">
            <span v-if="isLoading">
              <i class="fa-solid fa-spinner fa-spin me-2"></i>
              Resetting...
            </span>
            <span v-else>Reset Password</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useAuth } from '../composables/useAuth';
import { createConfettiParticles } from '../utils/confetti';
import ErrorAlert from './ErrorAlert.vue';

/* props / emits */
const props = defineProps({ show: Boolean });
const emit = defineEmits(['close', 'authenticated']);

/* auth */
const {
  signIn,
  signUp,
  confirmSignUp,
  forgotPassword,
  forgotPasswordSubmit,
  isLoading,
  error,
  clearError
} = useAuth();

/* FSM */
const state = ref('signin');

const transitions = {
  signin: {
    SIGNUP_REQUEST: 'signup',
    FORGOT_REQUEST: 'forgot',
    CONFIRM_REQUIRED: 'confirm',
    SIGNIN_SUCCESS: 'authenticated'
  },
  signup: {
    BACK: 'signin',
    CONFIRM_REQUIRED: 'confirm'
  },
  confirm: {
    VERIFIED: 'authenticated'
  },
  forgot: {
    CODE_SENT: 'reset'
  },
  reset: {
    RESET_COMPLETE: 'signin'
  }
};

function handleClose() {
  emit('close');
}

function handleBackdropClick() {
  // Allow closing on backdrop click
  emit('close');
}

function send(event) {
  const next = transitions[state.value]?.[event];
  if (!next) return;

  clearError();

  if (next === 'authenticated') {
    emit('authenticated');
    emit('close');
    state.value = 'signin';
    resetForm();
  } else {
    state.value = next;
  }
}

/* form */
const form = ref({
  email: '',
  password: '',
  confirmPassword: '',
  confirmationCode: '',
  resetCode: '',
  newPassword: ''
});

function resetForm() {
  Object.assign(form.value, {
    email: '',
    password: '',
    confirmPassword: '',
    confirmationCode: '',
    resetCode: '',
    newPassword: ''
  });
}

/* handlers */
async function onSignIn() {
  try {
    const result = await signIn(form.value.email, form.value.password);
    console.log('Sign in result:', result);
    
    if (result.isSignedIn) {
      send('SIGNIN_SUCCESS');
    } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
      send('CONFIRM_REQUIRED');
    } else {
      // Handle other cases like MFA, etc.
      console.log('Unhandled sign in step:', result.nextStep);
      // Stay in auth flow even for unhandled cases
    }
  } catch (err) {
    console.error('Sign in error:', err);
    // Error is already handled by useAuth, but stay in auth flow
    // Don't reset isInAuthFlow - let user see the error and try again
  }
}

async function onSignUp() {
  try {
    await signUp(form.value.email, form.value.password);
    send('CONFIRM_REQUIRED');
  } catch (err) {
    console.error('Sign up error:', err);
    // Error is already handled by useAuth
  }
}

async function onConfirm() {
  try {
    await confirmSignUp(form.value.email, form.value.confirmationCode);
    // After confirmation, try to sign in automatically
    const result = await signIn(form.value.email, form.value.password);
    if (result.isSignedIn) {
      send('VERIFIED');
    }
  } catch (err) {
    console.error('Confirmation error:', err);
    // Error is already handled by useAuth
  }
}

async function onForgot() {
  try {
    await forgotPassword(form.value.email);
    send('CODE_SENT');
  } catch (err) {
    console.error('Forgot password error:', err);
    // Error is already handled by useAuth
  }
}

async function onReset() {
  try {
    await forgotPasswordSubmit(
      form.value.email,
      form.value.resetCode,
      form.value.newPassword
    );
    send('RESET_COMPLETE');
  } catch (err) {
    console.error('Reset password error:', err);
    // Error is already handled by useAuth
  }
}

/* derived */
const title = computed(() => ({
  signin: 'Sign In',
  signup: 'Create Account',
  confirm: 'Verify Email',
  forgot: 'Reset Password',
  reset: 'Confirm Reset'
}[state.value]));

/* confetti */
const confettiCanvas = ref(null);
const containerWidth = ref(400);
const containerHeight = ref(600);
let confettiParticles = [];
let animationId = null;
let containerElement = null;
let devicePixelRatio = window.devicePixelRatio || 1;

function setupHighDPICanvas(canvas, width, height) {
  // Set the actual canvas size in memory (scaled up for high DPI)
  canvas.width = width * devicePixelRatio;
  canvas.height = height * devicePixelRatio;
  
  // Scale the canvas back down using CSS
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  
  // Scale the drawing context so everything draws at the correct size
  const ctx = canvas.getContext('2d');
  ctx.scale(devicePixelRatio, devicePixelRatio);
  
  return ctx;
}

function updateContainerSize() {
  if (!containerElement) {
    // Find the auth-container element
    containerElement = document.querySelector('.auth-container');
  }
  
  if (containerElement) {
    const rect = containerElement.getBoundingClientRect();
    containerWidth.value = rect.width;
    containerHeight.value = rect.height;
    
    // Update canvas for high DPI if it exists
    if (confettiCanvas.value) {
      setupHighDPICanvas(confettiCanvas.value, containerWidth.value, containerHeight.value);
    }
    
    // Update particle container dimensions
    confettiParticles.forEach(particle => {
      particle.containerWidth = containerWidth.value;
      particle.containerHeight = containerHeight.value;
    });
  }
}

function initConfetti() {
  if (!confettiCanvas.value) return;
  
  updateContainerSize();
  const ctx = setupHighDPICanvas(confettiCanvas.value, containerWidth.value, containerHeight.value);

  confettiParticles = createConfettiParticles(containerWidth.value, containerHeight.value, {
    particleCount: 20
  });

  function animate() {
    ctx.clearRect(0, 0, containerWidth.value, containerHeight.value);
    
    confettiParticles.forEach(particle => {
      particle.update();
      particle.draw(ctx);
    });

    animationId = requestAnimationFrame(animate);
  }

  animate();
}

function stopConfetti() {
  if (animationId) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }
}

// Watch for show prop changes to start/stop confetti
watch(() => props.show, async (newShow) => {
  if (newShow) {
    await nextTick();
    initConfetti();
  } else {
    stopConfetti();
  }
});

onMounted(() => {
  window.addEventListener('resize', updateContainerSize);
  if (props.show) {
    nextTick(() => initConfetti());
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateContainerSize);
  stopConfetti();
});
</script>

<style scoped>
/* Confetti Canvas */
.confetti-canvas-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  border-radius: 20px;
}

/* Modal Overlay - Centers content */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

/* Remove backdrop effects on mobile to prevent iOS issues */
@media (max-width: 768px) {
  .modal-overlay {
    background: var(--light-bg);
    backdrop-filter: none;
  }
}

/* Auth Modal Styles */
.auth-modal {
  max-width: 450px;
}

.auth-form-body {
  padding: 0;
}

/* Custom close button for auth modal */
.btn-close-custom {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--light-text-muted);
  font-size: 1.2rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 10;
}

.btn-close-custom:hover {
  background: var(--nested-container-bg);
  color: var(--light-text);
}

/* Form Control Styling */
.form-control {
  border-radius: 0.5rem;
  border: 2px solid var(--light-border);
  transition: all 0.2s ease;
  background-color: var(--light-card);
  color: var(--light-text);
  min-height: 50px;
}

.form-control:focus {
  background-color: var(--light-card);
  border-color: var(--light-text);
  color: var(--light-text);
  box-shadow: none;
}

.form-control::placeholder {
  color: var(--light-text-muted);
}

/* Alert Styling */
.alert {
  border-radius: 0.75rem;
  border: none;
  background: var(--nested-container-bg);
  color: var(--light-text);
  padding: 1rem;
}

.alert-info {
  background: rgba(54, 162, 235, 0.1);
  color: var(--light-text);
}

/* Button Styling */
.btn-primary {
  background-color: #36A2EB;
  border-color: #36A2EB;
  min-height: 50px;
  border-radius: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background-color: #2e8bc7;
  border-color: #2e8bc7;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  transform: none;
}

.btn-link {
  color: var(--light-text-muted);
  text-decoration: none;
  font-size: 0.9rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.btn-link:hover {
  color: var(--light-text);
  background: var(--nested-container-bg);
  text-decoration: none;
}

/* Landing Page Styles */
.auth-container {
  max-width: 400px;
  width: 100%;
  text-align: center;
  background: var(--light-card);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.auth-header {
  margin-bottom: 3rem;
  position: relative;
}

.auth-logo {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 8px 32px rgba(54, 162, 235, 0.3);
}

.auth-logo i {
  font-size: 2rem;
  color: white;
}

.auth-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--light-text);
  margin-bottom: 0.5rem;
}

.auth-subtitle {
  font-size: 1.1rem;
  color: var(--light-text-muted);
  margin-bottom: 0;
}

.auth-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 3rem;
  padding: 2rem;
  background: var(--nested-container-bg);
  border-radius: 16px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1rem;
  color: var(--light-text);
}

.feature-item i {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #36A2EB;
  font-size: 1.2rem;
}

.auth-actions {
  margin-top: 2rem;
}

.auth-actions .btn {
  width: 100%;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.auth-note {
  font-size: 0.9rem;
  color: var(--light-text-muted);
  line-height: 1.5;
  margin: 0;
}

/* Dark Mode Support */
:global([data-theme]) .auth-container {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

:global([data-theme]) .alert-info {
  background: rgba(54, 162, 235, 0.15);
}

/* Responsive Design */
@media (max-width: 576px) {
  .auth-container {
    padding: 1.5rem;
    margin: 1rem;
    max-width: calc(100% - 2rem);
  }

  .auth-title {
    font-size: 2rem;
  }

  .auth-subtitle {
    font-size: 1rem;
  }

  .auth-features {
    padding: 1.5rem;
  }

  .feature-item {
    font-size: 0.95rem;
  }

  .btn-close-custom {
    top: 0.75rem;
    right: 0.75rem;
  }
}

/* Animation */
.auth-container {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
